import {
  AppEntityHydrated,
  AssetEntityHydrated,
  EntityId,
  LambdaAbVariantEntity,
} from "@jackfruit/common"
import { DOMParser } from "@xmldom/xmldom"
import { withPrefix } from "gatsby"
import { trim } from "lodash"
import React from "react"
import { Helmet } from "react-helmet"
import Scroll from "react-scroll"
import { useEffectOnce } from "react-use"
import { useCanonical } from "~/hooks/useCanonical"
import { useMetaDescription } from "~/hooks/useMetaDescription"
import { useMetaTitle } from "~/hooks/useMetaTitle"
import { useOpenGraphImage } from "~/hooks/useOpenGraphImage"
import { useTemplateTextFonts } from "~/hooks/useTemplateTextFonts"
import { GA4fireAbTestAttribute } from "~/services/GA4"

const isDev = process.env.GATSBY_ACTIVE_ENV === "dev"

interface Props {
  config: AppEntityHydrated
  lambdaAbExperimentId?: string
  pageId?: EntityId
  seo?: {
    title?: string
    description?: string
    featureImage?: AssetEntityHydrated
    canonical?: string
  }
}
/**
 * Todo: Gatsby doc suggests us to use Gatsby Head api instead of Helmet
 * more info: https://www.gatsbyjs.com/docs/reference/built-in-components/gatsby-head/
 */

const Head: React.FC<Props> = ({
  config,
  lambdaAbExperimentId,
  pageId,
  seo,
}) => {
  const {
    baseUrl,
    language,
    openGraphTitle,
    openGraphDescription,
    settings: {
      abTestAttribute,
      googleMapKey,
      mapProvider,
      lambdaAbEnabled,
      lambdaAbExperimentId: globalExperimentId,
      lambdaAbVariants,
      rawHead,
      gaProjectId,
      gaMeasurementId,
      gaServerSideBaseUrl,
      gaServerSideProjectId,
      facebookMeasurementId,
      microsoftMeasurementId,
      pinterestMeasurementId,
      tiktokMeasurementId,
      upfluenceMeasurementId,
      isGTMServerSideEnabled,
      GTMServerSideOverrideUrl,
    },
  } = config

  const { fonts } = useTemplateTextFonts()

  // allow title and description override
  const [metaTitleOverride] = useMetaTitle(pageId ?? "0")
  const [metaDescriptionOverride] = useMetaDescription(pageId ?? "0")
  // allwo open graph image override
  const [openGraphImageOverride] = useOpenGraphImage(pageId ?? "0")
  // allow specific pages to force canonical
  const [canonical] = useCanonical(pageId ?? "0")
  const canonicalToUse = canonical || seo?.canonical

  const templateFonts = fonts
    ?.filter(({ ttf }) => {
      return ttf.fontFamily !== "Poppins"
    })
    .map(
      ({ ttf, woff2 }) => `
      @font-face {
        font-family: '${ttf.fontFamily}';
        font-style: ${ttf.fontStyle};
        font-weight: ${ttf.fontWeight};
        font-display: swap; 
        src: url(${ttf.src}) format('ttf');
      }
      @font-face {
        font-family: '${woff2.fontFamily}';
        font-style: ${woff2.fontStyle};
        font-weight: ${woff2.fontWeight};
        src: url(${woff2.src}) format('woff2');
        font-display: swap; 
        unicode-range: ${woff2.unicodeRange.join(" ")};
      }
  `
    )
    ?.join("")

  const baseUrlRedirect = trim(baseUrl, "/")
  const originUrl = new URL(baseUrlRedirect).origin
  const originUrlForGTMServerSide = GTMServerSideOverrideUrl
    ? trim(GTMServerSideOverrideUrl, "/")
    : originUrl

  const metaTitle = metaTitleOverride || seo?.title || openGraphTitle
  const metaDescription =
    metaDescriptionOverride || seo?.description || openGraphDescription

  const openGraphImagePath =
    seo?.featureImage?.path || openGraphImageOverride.url
  const openGraphImageWidth =
    seo?.featureImage?.width || openGraphImageOverride.width
  const openGraphImageHeight =
    seo?.featureImage?.height || openGraphImageOverride.height

  const parser = new DOMParser()
  const doc = Boolean(rawHead)
    ? parser.parseFromString(rawHead, "text/html")
    : ({} as Document)
  const rawScriptList = Boolean(rawHead)
    ? Array.from(doc.getElementsByTagName("script"))
    : []
  const rawStyle = Boolean(rawHead)
    ? doc.getElementsByTagName("style")[0]?.textContent
    : ""

  const isPreview = config.deployment?.type === "preview"
  const experimentId = globalExperimentId || lambdaAbExperimentId

  useEffectOnce(() => {
    if (window) {
      // Bootstrap GTM
      window.dataLayer = window.dataLayer ?? []

      // Global scroller for custom
      window.scrollToElement = (elementName: string) => {
        const scroller = Scroll.scroller
        scroller.scrollTo(elementName, {
          duration: 500,
          smooth: true,
          spy: true,
        })
      }
    }

    if (lambdaAbEnabled && experimentId) {
      let abTestVariantId: number = NaN
      if (config.deployment) {
        abTestVariantId = (lambdaAbVariants as LambdaAbVariantEntity[])
          .sort((a, b) => (a.id as number) - (b.id as number))
          .findIndex(
            lambdaAbVariant =>
              lambdaAbVariant.id === config.deployment.lambdaAbVariantId
          )
      }

      const abCookieAttribute =
        document.cookie.match(/X-Source=(.*?)(;|$)/)?.[1]
      const abTestAttr = abCookieAttribute ?? abTestAttribute

      GA4fireAbTestAttribute(abTestAttr, experimentId, abTestVariantId)
    }
  })

  const shouldMountGtmGA4 = Boolean(gaProjectId && !isDev)
  const shouldMountServersideGtmGA4 = Boolean(
    gaServerSideProjectId && gaServerSideBaseUrl && !isDev
  )

  const shouldShowMap = mapProvider === "googleMaps" && googleMapKey

  return (
    <Helmet>
      <html lang={language || "en-US"} translate="no" />
      <title>{metaTitle}</title>
      <link
        key="favicon-ico"
        rel="icon"
        type="image/x-icon"
        href={withPrefix("/favicon.ico")}
        sizes="any"
      />
      <link
        key="favicon-96x96"
        rel="icon"
        type="image/png"
        sizes="96x96"
        href={withPrefix("/favicon-96x96.png")}
      />
      <meta name="apple-mobile-web-app-title" content={metaTitle} />
      <meta name="viewport" content="width=device-width,user-scalable=no" />
      <meta name="name" content={metaTitle} />
      <meta name="description" content={metaDescription} />

      {Boolean(openGraphImagePath) && (
        <meta property="og:image" content={openGraphImagePath} />
      )}
      {Boolean(openGraphImageWidth) && (
        <meta property="og:image:width" content={`${openGraphImageWidth}`} />
      )}
      {Boolean(openGraphImageHeight) && (
        <meta property="og:image:height" content={`${openGraphImageHeight}`} />
      )}

      {Boolean(canonicalToUse) && (
        <link rel="canonical" href={canonicalToUse} />
      )}

      <base href={!isDev ? baseUrl : "/"} />

      <script
        src="https://www.google.com/recaptcha/enterprise.js?render=explicit"
        async
        defer
      />

      <script>
        {`
          if (!window.scrollToElement) {
            window.scrollToElement = function (elementName) {
              // do nothing
            }
          }
        `}
      </script>

      {facebookMeasurementId && (
        <link rel="dns-prefetch" href="https://connect.facebook.net" />
      )}

      {pinterestMeasurementId && (
        <link rel="dns-prefetch" href="https://ct.pinterest.com" />
      )}

      {tiktokMeasurementId && (
        <link rel="dns-prefetch" href="https://analytics.tiktok.com" />
      )}

      {microsoftMeasurementId && (
        <link rel="dns-prefetch" href="https://bat.bing.com" />
      )}

      {gaMeasurementId && (
        <script>{`
          window.dataLayer = window.dataLayer ?? []

          function gtag() {
            dataLayer.push(arguments);
          }
          dataLayer.push({
            GTM_VAR_GA4_MEASUREMENT_ID: "${
              isPreview
                ? config.deployment.previewGaMeasurementId
                : gaMeasurementId
            }",
            ${
              upfluenceMeasurementId
                ? `GTM_VAR_UPFLUENCE_MEASUREMENT_ID: "${upfluenceMeasurementId}",`
                : ""
            }
            FB_VAR_MEASUREMENT_ID: "${facebookMeasurementId}",
            MICROSOFT_VAR_MEASUREMENT_ID: "${microsoftMeasurementId}",
            PIN_VAR_MEASUREMENT_ID: "${pinterestMeasurementId}",
            TIKTOK_VAR_MEASUREMENT_ID: "${tiktokMeasurementId}",
          })
        `}</script>
      )}
      {!isGTMServerSideEnabled && shouldMountGtmGA4 && (
        <link rel="dns-prefetch" href="https://www.googletagmanager.com" />
      )}
      {!isGTMServerSideEnabled && shouldMountGtmGA4 && (
        <script
          async
        >{`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gaProjectId}');`}</script>
      )}
      {/* Server-side GTM */}
      {!isGTMServerSideEnabled && shouldMountServersideGtmGA4 && (
        <script async>
          {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer2'?'&l='+l:'';j.async=true;j.src=
          '${gaServerSideBaseUrl}/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer2','${gaServerSideProjectId}');`}
        </script>
      )}

      {isGTMServerSideEnabled && (
        <script
          async
        >{`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'${originUrlForGTMServerSide}/8febf188cb6d6e67e579d9ed1f22b830/?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gaProjectId}');`}</script>
      )}

      <script>{`
          if (
            window.location.href.indexOf("${baseUrlRedirect}") !== 0 &&
            window.location.href.indexOf("localhost") === -1
          ) {
            window.location.href = "${baseUrlRedirect}" + window.location.search
          }
        `}</script>

      {Boolean(templateFonts) && <style>{templateFonts}</style>}

      {shouldShowMap && (
        <link rel="dns-prefetch" href="https://maps.googleapis.com" />
      )}

      {shouldShowMap && (
        <link rel="dns-prefetch" href="https://maps.gstatic.com" />
      )}

      {shouldShowMap && (
        <script
          async
          type="text/javascript"
          src={`https://maps.googleapis.com/maps/api/js?key=${googleMapKey}&libraries=places,visualization`}
        />
      )}
      {Boolean(rawStyle) && <style type="text/css">{rawStyle}</style>}
      {rawScriptList.length > 0 &&
        rawScriptList?.map((raw, index) => {
          const srcUrl = raw.getAttribute("src") as string
          return srcUrl ? (
            <script key={index} src={srcUrl} async></script> //Support CDN
          ) : (
            <script key={index}>{raw.textContent}</script>
          )
        })}
    </Helmet>
  )
}

export default Head
